import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDownToLine, Plus, LayoutGrid, XCircle, Plug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApiIngestChat from './ApiIngestChat';
import ApiWorkspacePanel from './ApiWorkspacePanel';
import ConnectorCatalog from './ConnectorCatalog';

export default function ApiIngestDashboard() {
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [resumeApiId, setResumeApiId] = useState<string | null>(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showConnectors, setShowConnectors] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const handleConnectorInstalled = (apiId: string) => {
    setShowConnectors(false);
    setSearchParams({ api: apiId });
  };
  const [activeTab, setActiveTab] = useState('hub');

  const handleResumeApi = (apiId: string) => {
    setSessionId(null);
    setResumeApiId(apiId);
    setShowAssistant(true);
  };

  const workspaceEnabled = String(import.meta.env.VITE_API_INGEST_WORKSPACE_V2 || '1') === '1';

  return (
    <div className="h-full flex flex-col">
      {/* ── Top Navigation Bar ────────────────────────────────────── */}
      <div className="border-b bg-white px-6 pt-4 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center">
              <ArrowDownToLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">API Manager</h1>
              <p className="text-xs text-gray-500">Connect, configure, and monitor external data pipelines</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowConnectors((s) => !s)}
            >
              <Plug className="w-4 h-4 mr-1.5" />
              Connectors
            </Button>
            <Button
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => setShowAssistant((s) => !s)}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New API
            </Button>
          </div>
        </div>

        <nav className="flex gap-1 -mb-px">
          {[
            { id: 'hub', label: 'APIs', icon: LayoutGrid },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Content ───────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        {showConnectors && (
          <div className="mb-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative">
            <button
              onClick={() => setShowConnectors(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close connectors"
            >
              <XCircle className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <Plug className="w-5 h-5 text-orange-500" />
              Connectors
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Prebuilt provider connectors: endpoints and field mappings come from the catalog. Install, add credentials, then switch sync on.
            </p>
            <ConnectorCatalog onInstalled={handleConnectorInstalled} />
          </div>
        )}

        {showAssistant && (
          <div className="mb-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative">
            <button 
              onClick={() => setShowAssistant(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-orange-500" />
              API Setup Assistant
            </h2>
            <ApiIngestChat
              key={resumeApiId || 'default'}
              sessionId={sessionId}
              onSessionChange={(sid) => setSessionId(sid)}
              onPipelineReady={setActivePipelineId}
              resumeApiId={resumeApiId}
            />
          </div>
        )}

        {!workspaceEnabled ? (
          <div className="p-4 bg-blue-50 text-blue-800 rounded-lg border border-blue-200">
            <p className="font-semibold">API Manager workspace is disabled</p>
            <p className="text-sm mt-1">Enable `VITE_API_INGEST_WORKSPACE_V2` to use the unified API Manager workspace.</p>
          </div>
        ) : (
          <ApiWorkspacePanel
            onResumeApi={handleResumeApi}
            onPipelineSelect={setActivePipelineId}
            onOpenCreateAssistant={() => setShowAssistant(true)}
          />
        )}
      </div>
    </div>
  );
}
